import { useFrame } from '@react-three/fiber'
import { AssetVizHeatmapDisplayState, AssetVizMode } from 'features/assetViz'
import { useAssetVizInternal } from 'features/assetViz/AssetVizInternalProvider'
import { useSurfaceProps } from 'features/assetViz/objects/surfaces/util/SurfaceProvider'
import { Vec2, Vec4 } from 'features/assetViz/utils/helpers'
import { overlayMaterialDefines } from 'features/assetViz/utils/renderingDefines'
import { useLoadTexture } from 'features/assetViz/utils/useLoadTexture'
import { useColorScale } from 'features/dataLayers'
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import * as THREE from 'three'
import { Color } from 'three'
import {
  GeckoColorScaleThreshold,
  GeckoComponentDataFrame,
  GeckoComponentRect,
  GeckoMarkupLayer,
  GeckoMarkupPrimitive,
  GeckoView,
} from 'types'

import frag from './heatmap.frag'
import vert from './surface.vert'

// For live-reload of the shader file on save.
const key = THREE.MathUtils.generateUUID()

const MARKUP_SELECTION_OPACITY_MULTIPLIER = 0.666

/**
 The material used to render the colorful "heatmap" displayed on a component's
 surface. This might represent thickness, difference from nominal, or any
 number of other data layers.
 * @param chunkBounds         The bounds of the chunk, in component coordinates
 * @param doWrapX             Whether this chunk wraps in the x direction
 * @param doWrapY             Whether this chunk wraps in the y direction
 * @param cdf                 The CDF to render a heatmap of
 * @constructor
 */
export const HeatmapMaterial: FC<{
  chunkBounds: GeckoComponentRect
  doWrapX: boolean
  doWrapY: boolean
  cdf: GeckoComponentDataFrame
  geometry: GeckoComponentRect
  textureWidth: number
  textureHeight: number
  cdfData: undefined | Readonly<Float32Array>
  isLoading: boolean
}> = ({
  chunkBounds,
  doWrapX,
  doWrapY,
  cdf,
  cdfData,
  geometry,
  textureWidth,
  textureHeight,
  isLoading,
}) => {
  //----------------------------------------------------------------------------

  const {
    heatmapDisplayState,
    assetColorString,
    selection,
    getColorThresholdForComponent,
    setShowDataLoadingDisplay,
    clipConfig,
    cdfToFilteredValueRange,
    cdfToSelectedCategoricalValues,
    heatmapOpacity,
    setTextureLoading,
    offscreenRender,
  } = useAssetVizInternal()
  const { componentModel } = useSurfaceProps()

  const assetColor = useMemo(
    () => new Color(assetColorString).convertSRGBToLinear(),
    [assetColorString]
  )

  // Maximum number of categorical values that can be stored in the shader array
  // Note: when changing this, make sure to update heatmap.frag as well. GLSL prevents us from using a dynamic variable, so we need to manually update the hardcoded value.
  const MAX_ALLOWED_VALUES = 7

  // An object of measurement to pass down to the shader level. Note that this
  // object should never be recreated, only its measurement should be updated
  const uniforms = useMemo(
    () => ({
      // Component coords
      u_chunk_rect: { value: [0, 0, 0, 0] as Vec4 },
      u_wrap_dimensions: { value: [0, 0] as Vec2 },
      // Textures
      u_cdf_tex: { value: null as THREE.Texture | null },
      u_cdf_tex_rect: { value: [0, 0, 0, 0] as Vec4 },
      u_filtered_value_range: { value: [0, 0] as Vec2 },
      // Colors
      u_color_scale: { value: null as THREE.Texture | null },
      u_color_scale_range: { value: [0, 0] as Vec2 },
      u_non_selected_color: { value: [0, 0, 0, 0] as Vec4 },
      // Clipping
      u_clip_rect: { value: [0, 0, 0, 0] as Vec4 },
      // Categorical filtering
      u_allowed_categorical_values: {
        value: new Float32Array(MAX_ALLOWED_VALUES),
      },
      u_num_categorical_values_stored: { value: 0 },
    }),
    []
  )

  //----------------------------------------------------------------------------

  useMemo(() => {
    uniforms.u_chunk_rect.value = [
      chunkBounds.start.x.num,
      chunkBounds.start.y.num,
      chunkBounds.end.x.num,
      chunkBounds.end.y.num,
    ]
    uniforms.u_wrap_dimensions.value = [
      doWrapX ? chunkBounds.width.num : -1,
      doWrapY ? chunkBounds.height.num : -1,
    ]
  }, [uniforms, chunkBounds, doWrapX, doWrapY])

  useMemo(() => {
    uniforms.u_non_selected_color.value[0] = assetColor.r
    uniforms.u_non_selected_color.value[1] = assetColor.g
    uniforms.u_non_selected_color.value[2] = assetColor.b
  }, [uniforms, assetColor])

  useMemo(() => {
    uniforms.u_clip_rect.value = [
      clipConfig.unscaledRangeX?.[0] ?? -1e20,
      clipConfig.unscaledRangeY?.[0] ?? -1e20,
      clipConfig.unscaledRangeX?.[1] ?? 1e20,
      clipConfig.unscaledRangeY?.[1] ?? 1e20,
    ]
  }, [uniforms, clipConfig.unscaledRangeX, clipConfig.unscaledRangeY])

  //---------------------------------------------------------------------------

  // Only allocate a new buffer or image if the size of the data changes
  const cdfTexture = useMemo(() => {
    if (
      !isLoading &&
      cdfData !== undefined &&
      textureWidth * textureHeight !== cdfData?.length
    ) {
      console.error(
        `Texture width ${textureWidth} and height ${textureHeight} do not match length of data 
        array length of ${cdfData.length} as width * height = ${textureHeight * textureWidth}`
      )
    }
    const cdfTexture = new THREE.DataTexture(
      isLoading || !cdfData ? new Float32Array() : new Float32Array(cdfData),
      textureWidth,
      textureHeight,
      THREE.RedFormat,
      THREE.FloatType
    )
    cdfTexture.wrapS = THREE.ClampToEdgeWrapping
    cdfTexture.wrapT = THREE.ClampToEdgeWrapping
    cdfTexture.internalFormat = 'R32F'
    cdfTexture.needsUpdate = true
    cdfTexture.generateMipmaps = false
    return cdfTexture
  }, [textureWidth, textureHeight, cdfData, isLoading])

  //----------------------------------------------------------------------------

  const [effectiveColorThreshold, setEffectiveColorThreshold] = useState<
    | { loaded: false }
    | {
        loaded: true
        colorThreshold: GeckoColorScaleThreshold | undefined
      }
  >({ loaded: false })

  useEffect(() => {
    getColorThresholdForComponent(cdf).then((colorThreshold) =>
      setEffectiveColorThreshold({ colorThreshold, loaded: true })
    )
  }, [getColorThresholdForComponent, cdf])

  const {
    colorScale,
    colorScaleMin,
    colorScaleMax,
    isLoading: isColorScaleLoading,
  } = useColorScale(
    cdf,
    effectiveColorThreshold.loaded
      ? effectiveColorThreshold.colorThreshold
      : undefined
  )
  const colorScaleUrl = useMemo(() => colorScale.getTextureURL(), [colorScale])
  const { texture: colorScaleTexture, isLoading: colorScaleTextureIsLoading } =
    useLoadTexture(colorScaleUrl)
  const areColorsLoading = isColorScaleLoading || colorScaleTextureIsLoading
  useMemo(() => {
    if (!colorScaleTexture || areColorsLoading) return
    colorScaleTexture.wrapS = THREE.ClampToEdgeWrapping
    colorScaleTexture.wrapT = THREE.ClampToEdgeWrapping
    colorScaleTexture.magFilter = THREE.LinearFilter
    colorScaleTexture.minFilter = THREE.LinearFilter
    colorScaleTexture.needsUpdate = true
  }, [colorScaleTexture, areColorsLoading])

  //----------------------------------------------------------------------------

  const [showData, setShowData] = useState(false)

  useMemo(() => {
    cdfTexture.magFilter =
      heatmapDisplayState === AssetVizHeatmapDisplayState.Smooth
        ? THREE.LinearFilter
        : THREE.NearestFilter
    cdfTexture.minFilter =
      heatmapDisplayState === AssetVizHeatmapDisplayState.Smooth
        ? THREE.LinearFilter
        : THREE.NearestFilter
    cdfTexture.needsUpdate = true
  }, [cdfTexture, heatmapDisplayState])

  useMemo(() => {
    if (
      isLoading ||
      !colorScaleTexture ||
      colorScaleMin === undefined ||
      colorScaleMax === undefined ||
      areColorsLoading ||
      !effectiveColorThreshold.loaded
    ) {
      setShowDataLoadingDisplay(true) // We're currently loading
      setShowData(false) // Fade out the heatmap if it doesn't match the current CDF
      return
    }

    // Only update all textures simultaneously
    if (areColorsLoading) return

    setShowDataLoadingDisplay(false)
    setShowData(true) // Fade in the heatmap if it does match the current CDF

    const textureGeometry = geometry.project(chunkBounds.space)
    uniforms.u_cdf_tex.value = cdfTexture
    uniforms.u_cdf_tex_rect.value = [
      textureGeometry.start.x.num,
      textureGeometry.start.y.num,
      textureGeometry.end.x.num,
      textureGeometry.end.y.num,
    ]

    uniforms.u_color_scale.value = colorScaleTexture
    uniforms.u_color_scale_range.value = [colorScaleMin, colorScaleMax]

    const filteredValueRange = cdfToFilteredValueRange(cdf)
    const selectedCategoricalValues = cdfToSelectedCategoricalValues(cdf)

    const isCategoricalView =
      cdf.data.params.view.id === GeckoView.EMATCategory.id ||
      cdf.data.params.view.id === GeckoView.EmatCustomThicknessCondition.id

    if (isCategoricalView && selectedCategoricalValues) {
      const allowedValues = selectedCategoricalValues

      if (allowedValues.length === 0) {
        uniforms.u_num_categorical_values_stored.value = 0
      } else {
        uniforms.u_num_categorical_values_stored.value = Math.min(
          allowedValues.length,
          MAX_ALLOWED_VALUES
        )
        for (
          let i = 0;
          i < Math.min(allowedValues.length, MAX_ALLOWED_VALUES);
          i++
        ) {
          uniforms.u_allowed_categorical_values.value[i] = allowedValues[i]
        }
      }

      uniforms.u_filtered_value_range.value = [colorScaleMin, colorScaleMax]
    } else {
      // Use traditional range filtering
      const convertedFilteredRange = filteredValueRange?.convertTo(
        cdf.data.params.measurement_unit
      )
      let filterMin = convertedFilteredRange?.min.num ?? Number.NaN
      let filterMax = convertedFilteredRange?.max.num ?? Number.NaN
      const keyframes = [filterMin, filterMax, colorScaleMin, colorScaleMax]

      if (!isFinite(filterMin))
        filterMin = Math.min(...keyframes.filter(Number.isFinite))
      if (!isFinite(filterMax))
        filterMax = Math.max(...keyframes.filter(Number.isFinite))

      uniforms.u_filtered_value_range.value = [filterMin, filterMax]
      uniforms.u_num_categorical_values_stored.value = -1
    }

    setTextureLoading(false)
  }, [
    cdf,
    cdfTexture,
    chunkBounds,
    colorScaleMax,
    colorScaleMin,
    colorScaleTexture,
    areColorsLoading,
    cdfToFilteredValueRange,
    cdfToSelectedCategoricalValues,
    setShowDataLoadingDisplay,
    uniforms,
    isLoading,
    geometry,
    setTextureLoading,
    effectiveColorThreshold.loaded,
  ])

  //----------------------------------------------------------------------------

  const calculateTargetOpacity = useCallback(() => {
    // If textures are missing, target is 0
    if (!uniforms.u_cdf_tex.value || !uniforms.u_color_scale.value) {
      return 0
    }

    // If data isn't ready to show, target is 0
    if (!showData) {
      return 0
    }
    const assetHeatmapOpacity =
      typeof heatmapOpacity === 'number'
        ? heatmapOpacity
        : heatmapOpacity(componentModel)

    // If we have a markup selection, use reduced opacity
    if (
      (selection instanceof GeckoMarkupLayer ||
        selection instanceof GeckoMarkupPrimitive) &&
      !AssetVizMode.SelectMarkupRect
    ) {
      return MARKUP_SELECTION_OPACITY_MULTIPLIER * assetHeatmapOpacity
    }

    return assetHeatmapOpacity
  }, [
    uniforms.u_cdf_tex.value,
    uniforms.u_color_scale.value,
    showData,
    selection,
    heatmapOpacity,
    componentModel,
  ])

  useFrame((_, delta) => {
    const targetOpacity = calculateTargetOpacity()
    const currentOpacity = uniforms.u_non_selected_color.value[3]
    // Smoothly interpolate to target opacity
    uniforms.u_non_selected_color.value[3] = THREE.MathUtils.lerp(
      currentOpacity,
      targetOpacity,
      offscreenRender ? 1 : Math.min(1, delta * 10.0) // if rendering off-screen we want this transition to be instantaneous
    )
  })

  //----------------------------------------------------------------------------

  return (
    <shaderMaterial
      key={key}
      vertexShader={vert}
      fragmentShader={frag}
      uniforms={uniforms}
      {...overlayMaterialDefines}
    />
  )
}
